"use client";

import { CalendarClock, Sparkles, MessageCircle } from "lucide-react";
import { Button } from "@tripcart/traveler-ui/components/button";
import { cx } from "@tripcart/traveler-ui/cx";
import { useTranslator } from "@/lib/translation-context";

import styles from "./_not-configured-card.module.css";

/** Sidebar shown when the operator hasn't yet configured pricing or
 *  availability for an experience. Replaces — does not augment — the
 *  booking form, so travelers never see empty form fields with an
 *  enabled submit button (the shape that reads as "broken"). Anchors on
 *  a single CTA (`Ask a question`) which reuses the existing inquiry
 *  dialog, so no new lead-capture infrastructure is required. */
export function NotConfiguredCard({
  hasNoPricing,
  hasNoAvailability,
  onAskQuestion,
}: {
  hasNoPricing: boolean;
  hasNoAvailability: boolean;
  onAskQuestion: () => void;
}) {
  const t = useTranslator();
  // Pick the narrowest accurate description so the copy reflects what
  // the traveler is actually waiting on. `both` is the default and the
  // most common state for a tour that's just been created.
  const body =
    hasNoPricing && hasNoAvailability
      ? t(
          "Pricing and dates for this tour are still being set up. Reach out and we'll share the details as soon as they're ready.",
        )
      : hasNoPricing
        ? t(
            "Pricing for this tour is still being set up. Reach out and we'll share the details as soon as they're ready.",
          )
        : t(
            "Travel dates for this tour are still being set up. Reach out and we'll share the details as soon as they're ready.",
          );

  return (
    <div className={styles.card}>
      {/* Soft decorative orbs — purely visual, kept low-opacity so they
          read as gentle warmth rather than a "warning" tint. */}
      <div aria-hidden className={cx(styles.orb, styles.orbWarm)} />
      <div aria-hidden className={cx(styles.orb, styles.orbSoft)} />

      <div className={styles.content}>
        {/* Status pill — communicates state before the heading is read. */}
        <span className={styles.pill}>
          <Sparkles strokeWidth={2.25} />
          {t("Almost Ready")}
        </span>

        {/* Icon — softly tinted, sits in its own tile so it doesn't fight
            the orb glows for visual weight. */}
        <div className={styles.iconTile}>
          <CalendarClock strokeWidth={1.5} aria-hidden />
        </div>

        {/* Copy block — display headline, conversational body. Capped width
            keeps it readable inside the 320-ish-px sidebar column. */}
        <div className={styles.copy}>
          <h3 className={styles.title}>{t("Booking Opens Soon")}</h3>
          <p className={styles.body}>{body}</p>
        </div>

        {/* Single primary CTA — the tenant primary marks this as the one
            action on the card, an inquiry path while booking is unconfigured. */}
        <Button onClick={onAskQuestion} size="lg" className={styles.cta}>
          <MessageCircle strokeWidth={2} />
          {t("Ask a Question")}
        </Button>

        {/* No response-time line: this card shows for a tour still being set
            up, which by definition has no `onRequest.responseTime` to read (the
            on-request sidebar renders that one). Do not substitute a constant. */}
      </div>
    </div>
  );
}
